import React, { useEffect, useState } from 'react';
import './index.css';
import { AppProvider } from './context/AppContext';
import { api } from './api';
import Dashboard from './pages/Dashboard';
import OrderEntry from './pages/OrderEntry';
import Employees from './pages/Employees';
import Departments from './pages/Departments';
import Items from './pages/Items';
import Orders from './pages/Orders';
import Reports from './pages/Reports';
import Expenses from './pages/Expenses';
import Quotation from './pages/Quotation';
import Login from './pages/Login';

const PAGES = [
  { key: 'dashboard', icon: 'D', label: 'Dashboard', section: 'main' },
  { key: 'order', icon: '+', label: 'New Order', section: 'main' },
  { key: 'orders', icon: 'O', label: 'Order History', section: 'main' },
  { key: 'reports', icon: 'R', label: 'Reports', section: 'main' },
  { key: 'expenses', icon: '₹', label: 'Expenses', section: 'main' },
  { key: 'quotation', icon: 'Q', label: 'Quotation', section: 'main' },
  { key: 'employees', icon: 'E', label: 'Employees', section: 'admin' },
  { key: 'departments', icon: 'DP', label: 'Departments', section: 'admin' },
  { key: 'items', icon: 'M', label: 'Menu Items', section: 'admin' },
];

const PAGE_TITLES = {
  dashboard: { title: 'Dashboard', subtitle: 'IOCL MPSO Canteen Overview' },
  order: { title: 'New Order Entry', subtitle: 'Record food consumption for an employee' },
  orders: { title: 'Order History', subtitle: 'Browse and manage all recorded orders' },
  reports: { title: 'Reports & Analytics', subtitle: 'Monthly summaries and consumption analysis' },
  expenses: { title: 'Expenses & Staff', subtitle: 'Track daily expenses and staff salary payments' },
  quotation: { title: 'Quotation Builder', subtitle: 'Create printable item quotations' },
  employees: { title: 'Employee Management', subtitle: 'Add and manage canteen users' },
  departments: { title: 'Department Management', subtitle: 'Manage organisational departments' },
  items: { title: 'Menu Item Management', subtitle: 'Control prices and availability of canteen items' },
};

const COMPONENTS = {
  dashboard: Dashboard,
  order: OrderEntry,
  orders: Orders,
  reports: Reports,
  expenses: Expenses,
  quotation: Quotation,
  employees: Employees,
  departments: Departments,
  items: Items,
};

export default function App() {
  const [page, setPage] = useState('dashboard');
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [loginNotice, setLoginNotice] = useState('');
  const PageComp = COMPONENTS[page];
  const meta = PAGE_TITLES[page];

  const mainPages = PAGES.filter(p => p.section === 'main');
  const adminPages = PAGES.filter(p => p.section === 'admin');

  useEffect(() => {
    api.getSession()
      .then(session => setUser(session.authenticated ? session.user : null))
      .catch(() => setUser(null))
      .finally(() => setAuthLoading(false));
  }, []);

  // While logged in, keep an SSE stream open so a login from elsewhere with
  // this account kicks this tab out immediately instead of waiting for its
  // next API call to hit a 401.
  useEffect(() => {
    if (!user) return undefined;
    const source = api.openAuthEvents();
    source.addEventListener('force-logout', () => {
      source.close();
      setUser(null);
      setPage('dashboard');
      setLoginNotice('You were signed out because this account logged in from another device.');
    });
    return () => source.close();
  }, [user]);

  const handleLogout = async () => {
    await api.logout().catch(() => {});
    // Clear per-user client state on logout so the next login (even as a
    // different user on the same browser) doesn't inherit this session's
    // paid/unpaid ticks or any other in-memory scratch space.
    try {
      Object.keys(localStorage).forEach((k) => {
        if (k.startsWith('reportPaymentStatus:')) localStorage.removeItem(k);
      });
    } catch (_) { /* private mode / storage disabled — nothing to clear */ }
    setUser(null);
    setPage('dashboard');
  };

  const handleLogin = (loggedInUser) => {
    setLoginNotice('');
    setUser(loggedInUser);
  };

  if (authLoading) {
    return <div className="loading-center" style={{ minHeight: '100vh' }}><span className="spinner" /> Loading...</div>;
  }

  if (!user) {
    return (
      <AppProvider>
        <Login onLogin={handleLogin} notice={loginNotice} />
      </AppProvider>
    );
  }

  return (
    <AppProvider>
      <div className="app-shell">
        <aside className="sidebar">
          <div className="sidebar-logo">
            <h1>IOCL</h1>
            <span>Canteen Tracker - MPSO</span>
          </div>
          <nav className="sidebar-nav">
            <div className="nav-section-label">Main</div>
            {mainPages.map(p => (
              <div
                key={p.key}
                className={`nav-item ${page === p.key ? 'active' : ''}`}
                onClick={() => setPage(p.key)}
              >
                <span className="nav-icon">{p.icon}</span>
                {p.label}
              </div>
            ))}
            <div className="nav-section-label" style={{ marginTop: 8 }}>Admin</div>
            {adminPages.map(p => (
              <div
                key={p.key}
                className={`nav-item ${page === p.key ? 'active' : ''}`}
                onClick={() => setPage(p.key)}
              >
                <span className="nav-icon">{p.icon}</span>
                {p.label}
              </div>
            ))}
          </nav>
          <div className="sidebar-footer">
            Indian Oil Corporation<br />
            MP State Office - Bhopal<br />
            <span>v1.0 - Secure Instance</span>
          </div>
        </aside>

        <div className="main-content">
          <header className="page-header">
            <div>
              <h2>{meta.title}</h2>
              <div className="subtitle">{meta.subtitle}</div>
            </div>
            <div className="header-meta">
              <div>{new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
              <button className="btn btn-secondary btn-sm" onClick={handleLogout}>
                {`Logout ${user.username}`}
              </button>
            </div>
          </header>
          <div style={{ flex: 1, overflow: page === 'order' ? 'hidden' : 'auto', display: 'flex', flexDirection: 'column' }}>
            <PageComp user={user} />
          </div>
        </div>
      </div>
    </AppProvider>
  );
}
